/**
 * Privacy gate for every telemetry payload this app produces.
 *
 * The site's runtime state carries credentials: Payload connects to Postgres
 * with a DSN that holds the database password, form posts carry a person's
 * name, email, phone and message, requests carry cookies, and Drizzle wraps a
 * failed statement as `Failed query: select … where email = '…'`. Everything
 * here exists so error class, table, operation, route and status code reach
 * OneUptime while values that identify a person or authenticate a connection
 * do not.
 *
 * Rules for this module, in priority order:
 *
 *  1. Never throw. It runs inside the error path; an exception here would
 *     replace the real failure.
 *  2. Never invoke a getter. Reading `error.message` on a hostile object can
 *     execute application code, so error fields are read through own-property
 *     *data* descriptors only, and no value is ever coerced with `String()`.
 *  3. Bounded. Every string, object and array is clipped, so a 4 MB stack or a
 *     user-supplied object graph cannot become a 4 MB OTLP batch.
 *
 * @module lib/observability/sanitize
 */

/** Longest single string attribute kept. */
export const MAX_TEXT_LENGTH = 512
/** Longest stack trace kept. */
export const MAX_STACK_LENGTH = 2_048
/** Longest serialised object attribute kept. */
export const MAX_SERIALIZED_LENGTH = 1_024
/** Most keys taken from one object. */
export const MAX_ATTRIBUTES = 32
/** How deep nested values are followed before being replaced with a marker. */
export const MAX_DEPTH = 3

export const REDACTED = '[redacted]'
export const TRUNCATED = '[truncated]'
export const UNREADABLE = '[unreadable]'

type Primitive = string | number | boolean

/** Keys whose *value* is always a credential, whatever it looks like. */
const SENSITIVE_KEY =
  /(pass(word|wd)?|secret|token|authorization|cookie|credential|api[_-]?key|access[_-]?key|private[_-]?key|session[_-]?id|signature|jwt|bearer|dsn|connection[_-]?string|database[_-]?url|postgres[_-]?url)/i

/**
 * Error class names this codebase (or the runtime it runs on) can actually
 * throw. Anything else is reported as `Error`: a thrown object can carry any
 * own `name`, which would otherwise become an unbounded, attacker-controlled
 * `exception.type` dimension.
 */
const ERROR_NAME_ALLOWLIST: ReadonlySet<string> = new Set([
  // ECMAScript built-ins.
  'Error',
  'TypeError',
  'RangeError',
  'ReferenceError',
  'SyntaxError',
  'EvalError',
  'URIError',
  'AggregateError',
  // Web/Node platform names.
  'DOMException',
  'AbortError',
  'TimeoutError',
  'NetworkError',
  'NotFoundError',
  'NotAllowedError',
  'QuotaExceededError',
  // Payload CMS.
  'APIError',
  'ValidationError',
  'NotFound',
  'Forbidden',
  'Unauthorized',
  'AuthenticationError',
  'PayloadError',
  // Drizzle ORM.
  'DrizzleQueryError',
  'DrizzleError',
])

/**
 * Ordered redaction rules for free text. Each is anchored, bounded and
 * replacement-only, so a pathological input degrades to a clipped string
 * rather than a backtracking loop.
 */
const TEXT_RULES: ReadonlyArray<readonly [RegExp, string]> = [
  // scheme://user:password@host — the Postgres DSN Payload connects with.
  [/\b([a-z][a-z0-9+.-]{1,20}):\/\/[^\s/@"':]{1,120}:[^\s/@"']{1,200}@/gi, '$1://[redacted]@'],
  // Authorization header values.
  [/\b(Bearer|Basic|Token)\s+[A-Za-z0-9._~+/=-]{8,}/gi, '$1 [redacted]'],
  // JWTs — header.payload.signature.
  [/\beyJ[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}\.[A-Za-z0-9_-]{4,}/g, '[redacted-jwt]'],
  // Personal data that can appear inside an error message: the site's own forms
  // collect a name, email and phone, and Payload quotes the rejected row back.
  [/\b[A-Z0-9._%+-]+@[A-Z0-9.-]+\.[A-Z]{2,}\b/gi, '[redacted-email]'],
  // A run long enough to be a phone number or an account id; short numbers
  // (status codes, counts, ports) are left alone.
  [/\+?\d[\d\s().-]{8,}\d/g, '[redacted-number]'],
  // Vendor-shaped keys.
  [/\b(sk|pk|rk)_(live|test)_[A-Za-z0-9]{6,}/g, '[redacted-key]'],
  [/\bvercel_blob_rw_[A-Za-z0-9_]{6,}/g, '[redacted-key]'],
  // Tokens in a URL or query string (preview links, revalidation, reset flows).
  [
    /([?&;#](?:token|access_token|refresh_token|id_token|code|key|api[_-]?key|signature|sig|password|secret|session|jwt|state|reset|reset_token|invite|invite_token|share|share_token|auth)[^=&\s"']{0,40}=)([^&\s"'#]{1,512})/gi,
    '$1[redacted]',
  ],
  // key: value / key=value assignments in free text and JSON dumps.
  [
    /\b(password|passwd|pwd|secret|token|api[_-]?key|access[_-]?key|client[_-]?secret|authorization|cookie|set-cookie)\b\s*([:=])\s*("[^"]{0,512}"|'[^']{0,512}'|[^\s,;}]{1,512})/gi,
    '$1$2[redacted]',
  ],
  // Postgres detail lines quote the offending value: Key (email)=(a@b.c).
  [/(Key\s*\([^)]{1,80}\))=\([^)]{0,512}\)/g, '$1=([redacted])'],
  // SQL literal VALUES( … ) — bind placeholders ($1) are kept, inline literals are not.
  [/(\bvalues\s*\()[^)]{1,512}(\))/gi, '$1[redacted]$2'],
]

const SQL_SHAPED = /\b(select|insert\s+into|update|delete\s+from|values\s*\(|from\s+[a-z_"])/i

/**
 * Postgres and Drizzle quote values back at the caller — both in `DETAIL:`
 * lines and in Drizzle's "Failed query: SELECT … WHERE email = '…'" wrapper.
 * Quoted literals are replaced whenever the string reads like SQL; non-SQL text
 * (English in quotes) is left alone so diagnostics stay readable.
 */
function redactSqlLiterals(value: string): string {
  if (!SQL_SHAPED.test(value)) return value
  return value
    .replace(/'(?:[^'\\]|\\.){0,512}'/g, "'?'")
    .replace(/\$\$[^$]{0,512}\$\$/g, '$redacted$')
}

/**
 * A whole statement inside free text is a statement literal, not a diagnostic
 * shape: Drizzle wraps every driver failure as
 * `Failed query: SELECT "site_form_submissions"."email" FROM …`. The operation
 * is reported separately on the failure record, so the body is replaced.
 */
function redactSqlStatements(value: string): string {
  return value
    .replace(/\bFailed query:\s*[\s\S]{0,4000}/gi, 'Failed query: [redacted-sql]')
    .replace(
      /\b(select|insert\s+into|update|delete\s+from)\b[\s\S]{0,1000}?\b(from|where|values|set)\b[\s\S]{0,1000}/gi,
      '[redacted-sql]',
    )
}

/** Strip control characters that would break OTLP JSON encoding. */
function stripControlCharacters(value: string): string {
  return value.replace(/[\u0000-\u0008\u000b\u000c\u000e-\u001f\u007f]/g, ' ')
}

function clip(value: string, maxLength: number): string {
  if (value.length <= maxLength) return value
  return `${value.slice(0, maxLength)}…${TRUNCATED}`
}

/**
 * Redact one string. Never throws: a failure of the rules themselves returns a
 * marker instead of the raw value.
 */
export function sanitizeText(input: string, maxLength: number = MAX_TEXT_LENGTH): string {
  if (typeof input !== 'string') return ''
  let output: string
  try {
    output = redactSqlLiterals(input)
    output = redactSqlStatements(output)
    for (const [pattern, replacement] of TEXT_RULES) {
      output = output.replace(pattern, replacement)
    }
  } catch {
    output = UNREADABLE
  }
  return clip(stripControlCharacters(output), maxLength)
}

/**
 * Stacks carry framework frames (fine) and, on some errors, a dump of the
 * request that produced them — headers, cookies, form fields (not fine).
 * Frames are kept so a minified bundle frame can still be resolved through an
 * uploaded source map; anything that looks like a header block is dropped.
 */
export function sanitizeStack(stack: string | undefined): string {
  if (typeof stack !== 'string' || stack.length === 0) return ''
  const lines = stack.split('\n')
  const kept: string[] = []
  let skippingBlock = false
  for (const line of lines) {
    const trimmed = line.trim()
    if (
      /^(headers|request headers|response headers|cookies?|authorization|form data|body)\b/i.test(
        trimmed,
      )
    ) {
      skippingBlock = true
      continue
    }
    if (skippingBlock) {
      // A header block is indented or `key: value`; a stack frame starts with `at `.
      if (/^at\s/.test(trimmed) || trimmed === '' || !/^[\w.-]+:\s/.test(trimmed)) {
        skippingBlock = false
      } else {
        continue
      }
    }
    kept.push(line)
  }
  return clip(sanitizeText(kept.join('\n'), MAX_STACK_LENGTH), MAX_STACK_LENGTH)
}

/** True when the key name itself marks the value as a credential. */
export function isSensitiveKey(key: string): boolean {
  return SENSITIVE_KEY.test(key)
}

/**
 * Coerce a thrown value's `name` into a bounded, allowlisted class name.
 * Reads a data descriptor only (never a getter) and never coerces a non-string
 * value, so an object with `get name()` or `name: { toString() {…} }` cannot run
 * code here.
 */
export function sanitizeErrorName(name: unknown): string {
  if (typeof name !== 'string' || name.length === 0) return 'Error'
  const cleaned = stripControlCharacters(name).slice(0, 80)
  return ERROR_NAME_ALLOWLIST.has(cleaned) ? cleaned : 'Error'
}

interface DataProperty {
  readonly value: unknown
}

/**
 * Read one own property *without* running a getter. Accessors are ignored on
 * purpose: a hostile `get message()` must never be invoked from the error path.
 */
export function readDataProperty(target: unknown, key: string): DataProperty | undefined {
  let current: unknown = target
  for (let depth = 0; depth < 8; depth += 1) {
    if (typeof current !== 'object' || current === null) return undefined
    try {
      const descriptor = Object.getOwnPropertyDescriptor(current, key)
      if (descriptor) {
        if ('value' in descriptor) return { value: descriptor.value }
        return undefined
      }
      current = Object.getPrototypeOf(current)
    } catch {
      return undefined
    }
  }
  return undefined
}

export interface SafeErrorInfo {
  readonly name: string
  readonly message: string
  readonly stack: string
}

/**
 * Extract name/message/stack from anything thrown, invoking no getters and
 * never throwing. `name` is allowlisted; message and stack are redacted and
 * clipped.
 */
export function safeErrorInfo(error: unknown): SafeErrorInfo {
  if (typeof error === 'string') {
    return { name: 'Error', message: sanitizeText(error), stack: '' }
  }
  if (error === null || error === undefined) {
    return { name: 'Error', message: UNREADABLE, stack: '' }
  }
  if (typeof error !== 'object') {
    // Primitives only; String() on a symbol is allowed, on an object would not be.
    const kind = typeof error
    const text =
      kind === 'number' || kind === 'boolean' || kind === 'bigint'
        ? `${error}`
        : kind === 'symbol'
          ? error.toString()
          : UNREADABLE
    return { name: 'Error', message: sanitizeText(text), stack: '' }
  }

  const nameValue = readDataProperty(error, 'name')?.value
  const messageValue = readDataProperty(error, 'message')?.value
  const stackValue = readDataProperty(error, 'stack')?.value

  const message =
    typeof messageValue === 'string'
      ? sanitizeText(messageValue)
      : typeof messageValue === 'number' || typeof messageValue === 'boolean'
        ? `${messageValue}`
        : UNREADABLE

  return {
    name: sanitizeErrorName(nameValue),
    message,
    stack: typeof stackValue === 'string' ? sanitizeStack(stackValue) : '',
  }
}

/**
 * A bounded, fully sanitized one-line summary for the places that must write to
 * the process console (which, on Vercel, is a remote sink). Never returns a raw
 * message, stack, promise or object.
 */
export function safeErrorSummary(error: unknown, maxLength: number = 240): string {
  const info = safeErrorInfo(error)
  return clip(sanitizeText(`${info.name}: ${info.message}`, maxLength), maxLength)
}

/**
 * Make an arbitrary value safe to serialise into a single OTLP attribute.
 * Objects are walked as far as MAX_DEPTH; functions, symbols and accessors are
 * dropped rather than evaluated.
 */
export function sanitizeValue(value: unknown, depth = 0): unknown {
  if (value === null || value === undefined) return value
  const kind = typeof value
  if (kind === 'string') return sanitizeText(value as string)
  if (kind === 'number') return Number.isFinite(value as number) ? value : UNREADABLE
  if (kind === 'boolean') return value
  if (kind === 'bigint') return `${value}`
  if (kind === 'function' || kind === 'symbol') return undefined
  if (depth >= MAX_DEPTH) return TRUNCATED

  try {
    if (value instanceof Date) return value.toISOString()
    if (value instanceof Error) {
      const info = safeErrorInfo(value)
      return { name: info.name, message: info.message, stack: info.stack }
    }
    if (Array.isArray(value)) {
      return value.slice(0, 10).map((item) => sanitizeValue(item, depth + 1))
    }
    return sanitizeProperties(value as object, depth)
  } catch {
    return UNREADABLE
  }
}

function sanitizeProperties(source: object, depth: number): Record<string, unknown> {
  const out: Record<string, unknown> = {}
  let names: string[]
  try {
    names = Object.keys(source)
  } catch {
    return { value: UNREADABLE }
  }
  for (const key of names.slice(0, MAX_ATTRIBUTES)) {
    let property: DataProperty | undefined
    try {
      const descriptor = Object.getOwnPropertyDescriptor(source, key)
      // Ignore accessors entirely — reading them would execute application code.
      property = descriptor && 'value' in descriptor ? { value: descriptor.value } : undefined
    } catch {
      property = undefined
    }
    if (!property) continue
    const safeKey = clip(stripControlCharacters(key), 64)
    out[safeKey] = isSensitiveKey(key) ? REDACTED : sanitizeValue(property.value, depth + 1)
  }
  return out
}

/**
 * Flatten a caller's context into OTLP-safe attributes: primitives stay
 * primitive, everything else is serialised *after* sanitising, and credentials
 * are dropped by key name as well as by content.
 */
export function sanitizeAttributes(
  source: Record<string, unknown> | undefined,
  prefix = '',
): Record<string, Primitive> {
  const out: Record<string, Primitive> = {}
  if (!source) return out
  let names: string[]
  try {
    names = Object.keys(source)
  } catch {
    return out
  }
  for (const key of names.slice(0, MAX_ATTRIBUTES)) {
    const descriptor = (() => {
      try {
        return Object.getOwnPropertyDescriptor(source, key)
      } catch {
        return undefined
      }
    })()
    if (!descriptor || !('value' in descriptor)) continue

    const attributeKey = clip(`${prefix}${stripControlCharacters(key)}`, 96)
    if (isSensitiveKey(key)) {
      out[attributeKey] = REDACTED
      continue
    }
    const value = descriptor.value
    if (typeof value === 'string') {
      out[attributeKey] = sanitizeText(value)
    } else if (typeof value === 'number' && Number.isFinite(value)) {
      out[attributeKey] = value
    } else if (typeof value === 'boolean') {
      out[attributeKey] = value
    } else if (value === null || value === undefined) {
      continue
    } else {
      let serialized: string
      try {
        serialized = JSON.stringify(sanitizeValue(value))
      } catch {
        serialized = UNREADABLE
      }
      out[attributeKey] = clip(serialized || UNREADABLE, MAX_SERIALIZED_LENGTH)
    }
  }
  return out
}
